/**
 * 3 次元ベクトル。座標系は右手系・Y 軸上向き・1 単位 = 1 m（ADR-0011）。
 *
 * 値は不変として扱い、演算は常に新しいベクトルを返す。
 * 大量の点を扱う処理（サンプル軌道など）では `Float64Array` を使い、この型は境界や少数の値に用いる。
 */
export type Vec3 = readonly [x: number, y: number, z: number];

/** ベクトルを作成する。 */
export function vec3(x: number, y: number, z: number): Vec3 {
  return [x, y, z];
}

/** 零ベクトル。 */
export const ZERO: Vec3 = Object.freeze([0, 0, 0] as const);

/** 鉛直上向きの単位ベクトル（+Y）。 */
export const UP: Vec3 = Object.freeze([0, 1, 0] as const);

/** `a + b` */
export function add(a: Vec3, b: Vec3): Vec3 {
  return [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
}

/** `a - b` */
export function sub(a: Vec3, b: Vec3): Vec3 {
  return [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
}

/** `v * k` */
export function scale(v: Vec3, k: number): Vec3 {
  return [v[0] * k, v[1] * k, v[2] * k];
}

/** 内積 `a · b` */
export function dot(a: Vec3, b: Vec3): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

/** 外積 `a × b`（右手系） */
export function cross(a: Vec3, b: Vec3): Vec3 {
  return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
}

/** ベクトルの長さ */
export function length(v: Vec3): number {
  return Math.hypot(v[0], v[1], v[2]);
}

/** 2 点間の距離 */
export function distance(a: Vec3, b: Vec3): number {
  return length(sub(a, b));
}

/**
 * 単位ベクトルを返す。
 *
 * @throws {RangeError} 長さが 0（または非有限）の場合。呼び出し側で退化した入力を扱うこと。
 */
export function normalize(v: Vec3): Vec3 {
  const len = length(v);
  if (!(len > 0) || !Number.isFinite(len)) {
    throw new RangeError(`長さ ${String(len)} のベクトルは正規化できません`);
  }
  return scale(v, 1 / len);
}

/** 線形補間 `a + (b - a) t` */
export function lerp(a: Vec3, b: Vec3, t: number): Vec3 {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
}

/** 各成分の差が `epsilon` 以下なら等しいとみなす。 */
export function approxEquals(a: Vec3, b: Vec3, epsilon = 1e-9): boolean {
  return (
    Math.abs(a[0] - b[0]) <= epsilon &&
    Math.abs(a[1] - b[1]) <= epsilon &&
    Math.abs(a[2] - b[2]) <= epsilon
  );
}
